/** Top-level CSV → Transaction[] pipeline with ingestion limits. */

import type { ParseResult, Transaction } from '../types';
import { detectCurrency, parseAmount } from './amounts';
import { detectDayFirst, parseDate } from './dates';
import { inferColumns } from './columns';
import { parseRaw } from './parser';

/** Hard ingestion caps — a transactions export has no business exceeding these. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_ROWS = 250_000;

export class CsvError extends Error {}

export function parseTransactions(text: string): ParseResult {
  const rows = parseRaw(text);
  if (rows.length < 2) throw new CsvError('File has no data rows.');
  if (rows.length - 1 > MAX_ROWS) {
    throw new CsvError(`File has ${rows.length - 1} rows — the limit is ${MAX_ROWS.toLocaleString()}.`);
  }

  const headers = (rows[0] ?? []).map((h) => h.trim());
  const dataRows = rows.slice(1);
  const idx = inferColumns(headers, dataRows.slice(0, 25));

  if (idx.date === -1) throw new CsvError('Could not find a date column.');
  if (idx.amount === -1 && idx.debit === -1) throw new CsvError('Could not find an amount column.');
  if (idx.desc === -1) throw new CsvError('Could not find a description/merchant column.');

  const dayFirst = detectDayFirst(dataRows, idx.date);
  const raw: Transaction[] = [];
  const rawAmountStrings: string[] = [];
  let skipped = 0;

  for (const r of dataRows) {
    const date = parseDate(r[idx.date], dayFirst);
    let amount: number | null = null;
    if (idx.debit !== -1) {
      const debit = parseAmount(r[idx.debit]);
      const credit = idx.credit !== -1 ? parseAmount(r[idx.credit]) : null;
      if (debit != null && debit !== 0) amount = -Math.abs(debit);
      else if (credit != null && credit !== 0) amount = Math.abs(credit);
      rawAmountStrings.push(String(r[idx.debit] ?? ''));
    } else {
      amount = parseAmount(r[idx.amount]);
      rawAmountStrings.push(String(r[idx.amount] ?? ''));
    }
    const description = String(r[idx.desc] ?? '').trim();
    if (!date || amount == null || !description) {
      skipped++;
      continue;
    }
    raw.push({ date, description, amount });
  }

  if (!raw.length) throw new CsvError('No parseable transactions found in this file.');

  // Sign convention: when both signs are present, negatives are spend;
  // an all-positive file is assumed to be all spend.
  const hasNegatives = raw.some((t) => t.amount < 0);
  const transactions = raw
    .filter((t) => (hasNegatives ? t.amount < 0 : t.amount > 0))
    .map((t) => ({ ...t, amount: Math.abs(t.amount) }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (!transactions.length) throw new CsvError('No spend transactions found in this file.');

  const warnings: string[] = [];
  if (skipped > 0) warnings.push(`${skipped} row${skipped === 1 ? '' : 's'} skipped (unparseable date/amount).`);

  return { transactions, warnings, currency: detectCurrency(rawAmountStrings.slice(0, 50)) };
}
